import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { FileSpreadsheet, Trash2, UserCog, Clock, ArrowUpFromLine, TicketCheck, Settings, RotateCw, Calendar } from 'lucide-react';
import { UserManagementDialog } from '../UserManagement/UserManagementDialog';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { fetchCatImage } from '@/utils/catApi';

interface AdminActionsProps {
  isMobile?: boolean;
  onLogOpen: () => void;
  onExport: () => void;
  onImport: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearDatabase: () => void;
  onManageTemplates: () => void;
  onShowEmailLogs: () => void;
  onManageUsers: () => void;
  onWeeklyCalendar: () => void;
  onWeeklyReset?: () => Promise<{totalCoins: number, resetCount: number}>;
}

export const AdminActions = ({ 
  isMobile,
  onLogOpen,
  onExport,
  onImport,
  onClearDatabase,
  onManageTemplates,
  onShowEmailLogs,
  onManageUsers,
  onWeeklyCalendar,
  onWeeklyReset
}: AdminActionsProps) => {
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isWeeklyResetDialogOpen, setIsWeeklyResetDialogOpen] = useState(false);
  const [weeklyResetStats, setWeeklyResetStats] = useState<{totalCoins: number, resetCount: number} | null>(null);
  const [catImage, setCatImage] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const handleFileInputChange = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const syntheticEvent = {
      target: input,
      currentTarget: input,
      bubbles: e.bubbles,
      cancelable: e.cancelable,
      defaultPrevented: e.defaultPrevented,
      eventPhase: e.eventPhase,
      isTrusted: e.isTrusted,
      timeStamp: e.timeStamp,
      type: e.type,
      nativeEvent: e,
      isDefaultPrevented: () => e.defaultPrevented,
      isPropagationStopped: () => false,
      persist: () => {},
    } as React.ChangeEvent<HTMLInputElement>;
    onImport(syntheticEvent);
    setIsOpen(false);
  };

  const handleMenuItemClick = (callback: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    callback();
    setIsOpen(false);
  };

  const handleUserManagement = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUserManagementOpen(true);
    setIsOpen(false);
  };

  const handleFileUpload = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = handleFileInputChange;
    input.click();
    setIsOpen(false);
  };


  const handleWeeklyReset = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onWeeklyReset && !isResetting) {
      setIsResetting(true);
      
      try {
        const imageUrl = await fetchCatImage();
        setCatImage(imageUrl);
        
        const result = await onWeeklyReset();
        setWeeklyResetStats(result);
        
        setIsWeeklyResetDialogOpen(true);
      } catch (error) {
        console.error('Error during weekly reset:', error);
      } finally {
        setIsResetting(false);
        setIsOpen(false);
      }
    }
  };

  const handleCloseResetDialog = () => {
    setIsWeeklyResetDialogOpen(false);
    setWeeklyResetStats(null);
    setCatImage(null);
  };

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className={`${isMobile ? 'h-12 w-12' : 'h-16 w-16'} rounded-full bg-secondary hover:bg-secondary/80 shadow-md`}
            onClick={(e) => e.stopPropagation()}
          >
            <Settings className="h-6 w-6" />
            <span className="sr-only">Admin actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DropdownMenuLabel>Admin Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleMenuItemClick(onLogOpen)}>
            <Clock className="mr-2 h-4 w-4" />
            View Task Log
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleMenuItemClick(onExport)}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Export to CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleFileUpload}>
            <ArrowUpFromLine className="mr-2 h-4 w-4" />
            Import from CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleMenuItemClick(onClearDatabase)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Clear Database
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleMenuItemClick(onManageTemplates)}>
            <TicketCheck className="mr-2 h-4 w-4" />
            Manage Templates
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleMenuItemClick(onShowEmailLogs)}>
            <Clock className="mr-2 h-4 w-4" />
            Show Email Logs
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleUserManagement}>
            <UserCog className="mr-2 h-4 w-4" />
            User Management
          </DropdownMenuItem>
          {onWeeklyReset && (
            <DropdownMenuItem onClick={handleWeeklyReset} disabled={isResetting}>
              <RotateCw className="mr-2 h-4 w-4" />
              Weekly Reset
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleMenuItemClick(onWeeklyCalendar)}>
            <Calendar className="mr-2 h-4 w-4" />
            יומן שבועי
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <UserManagementDialog 
        open={isUserManagementOpen} 
        onOpenChange={setIsUserManagementOpen} 
      />

      <Dialog open={isWeeklyResetDialogOpen} onOpenChange={setIsWeeklyResetDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogTitle className="text-xl font-semibold text-center">האיפוס השבועי הושלם! 🎉</DialogTitle>
          <div className="flex flex-col items-center space-y-4">
            {weeklyResetStats && (
              <div className="text-center space-y-2">
                <p className="text-lg font-medium">
                  אספת <span className="text-cat-primary font-bold">{weeklyResetStats.totalCoins}</span> מטבעות השבוע!
                </p>
                <p className="text-gray-600">
                  {weeklyResetStats.resetCount} משימות אופסו למצב לא הושלם.
                </p>
              </div>
            )}
            
            {catImage && (
              <img 
                src={catImage} 
                alt="Weekly Reset Celebration Cat" 
                className="rounded-lg max-h-[300px] w-auto object-contain animate-fade-in"
              />
            )}
            
            <Button onClick={handleCloseResetDialog} className="mt-4">
              סגור
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
